/* eslint-disable indent */
/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles, Tooltip, Typography } from '@material-ui/core';
import {
  AccessAlarm,
  InfoOutlined,
  NotificationsActive,
  Subject,
} from '@material-ui/icons';
import clsx from 'clsx';
import DatePicker from 'components/DatePicker';
import TextEditor from 'components/TextEditor';
import ToggleCard from 'components/ToggleCard';
import i18n from 'i18n';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import { ClientFields } from 'modules/clients/interfaces';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import GenericForm from 'skyone-lib/lib/GenericForm';
import ApplicationState from 'store/interfaces';
import {
  adjustDateByTwoHours,
  formatDateToISO,
  formatDateWithMoment,
} from 'utils/helpers';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import styles from './styles';

const useStyles = makeStyles(styles);

export interface MaintenanceDisplay {
  active: boolean;
  start_date: string | null;
  end_date: string | null;
  message: any;
  display_to_logged_users: boolean;
}

export const displayInitialValues = {
  active: false,
  start_date: null,
  end_date: null,
  message: '',
  display_to_logged_users: false,
};

export interface Props {
  clientSelected: ClientFields | null;
  companySelected: CompanyData | null;
  defaultValue: MaintenanceDisplay;
  disabled?: boolean;
  role?: RolesEnum;
  onChange: (values: MaintenanceDisplay) => void;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
}

const Form = ({
  clientSelected,
  defaultValue,
  role,
  companySelected,
  disabled,
  onChange,
  setHasError,
}: Props) => {
  const classes = useStyles();

  const [startDateValue, setStartDateValue] = React.useState<any>(
    defaultValue?.start_date
  );
  const [endDateValue, setEndDateValue] = React.useState<any>(
    defaultValue?.end_date
  );

  const handleChange = (
    values: MaintenanceDisplay,
    toggle = true,
    shouldChangeDefaultValues = true
  ) => {
    const newValues = { ...values, active: toggle };

    // eslint-disable-next-line no-unused-expressions
    shouldChangeDefaultValues && onChange(newValues);
  };

  return (
    <ToggleCard
      active={defaultValue?.active}
      id="toggle-show-alert"
      title={i18n.t('SHOW_ALERT_KEY')}
      getActive={(value: boolean) => handleChange(defaultValue, !value)}
      customSubheader={
        clientSelected?.environment?.display_warning_enabled && (
          <Tooltip
            title={`${i18n.t('ENV_DISPLAY_WARNING_ENABLED_TOOLTIP_KEY')} ${
              clientSelected?.environment?.display_warning_end_datetime
                ? i18n.t('ENV_DISPLAY_WARNING_ENABLED_TOOLTIP_DATE_KEY', {
                    date: formatDateWithMoment(
                      clientSelected?.environment?.display_warning_end_datetime
                    ),
                  })
                : ''
            }`}
          >
            <Grid style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Typography className={classes.customSubheaderText}>
                {i18n.t('ENV_DISPLAY_WARNING_ENABLED_KEY')}
              </Typography>
              <InfoOutlined className={classes.customSubheaderIcon} />
            </Grid>
          </Tooltip>
        )
      }
      icon={
        <NotificationsActive
          className={clsx({
            [classes.iconGreen]: inAdminView(role, companySelected),
            [classes.iconGray]: inSuperAdminView(role, companySelected),
            [classes.iconBlue]: inOperatorView(role, companySelected),
            [classes.iconYellow]: companySelected,
          })}
        />
      }
      disable={disabled}
    >
      <Grid container className={classes.FormContainer}>
        <AccessAlarm className={classes.iconMaintenance} />
        <Grid item className={classes.datePicker}>
          <DatePicker
            id="display-start_date"
            type="datetime"
            label={i18n.t('START_ALERT_KEY')}
            disablePast
            minDate={new Date()}
            value={defaultValue?.start_date}
            keyValue="start_date"
            error={
              adjustDateByTwoHours(startDateValue) <
                formatDateToISO(new Date()) || !moment(startDateValue).isValid()
            }
            setFieldsValue={(key, value) => {
              handleChange(
                { ...defaultValue, [key]: value, end_date: null },
                true
              );
              setStartDateValue(value);
            }}
            disabled={disabled}
            onError={() =>
              setHasError(
                adjustDateByTwoHours(startDateValue) <
                  formatDateToISO(new Date()) ||
                  !moment(startDateValue).isValid()
              )
            }
          />
        </Grid>
        <Grid item className={classes.datePicker}>
          <DatePicker
            id="display-end_date"
            type="datetime"
            label={i18n.t('END_ALERT_KEY')}
            disablePast
            value={defaultValue?.end_date}
            error={
              adjustDateByTwoHours(endDateValue) <
                adjustDateByTwoHours(startDateValue) ||
              !moment(endDateValue).isValid()
            }
            minDate={adjustDateByTwoHours(startDateValue)}
            keyValue="end_date"
            setFieldsValue={(key, value) => {
              handleChange({ ...defaultValue, [key]: value }, true);
              setEndDateValue(value);
            }}
            disabled={disabled}
            onError={() =>
              setHasError(
                adjustDateByTwoHours(endDateValue) <
                  adjustDateByTwoHours(startDateValue) ||
                  !moment(endDateValue).isValid()
              )
            }
          />
        </Grid>
        <GenericForm
          overrideClasses={{ checkboxLabel: { fontSize: 12, fontWeight: 500 } }}
          errors={[]}
          values={defaultValue}
          setFieldValue={(key, value) =>
            handleChange({ ...defaultValue, [key]: value }, defaultValue.active)
          }
          screenFields={[
            {
              gridXs: 12,
              label: i18n.t('DISPLAY_TO_LOGGED_USERS_KEY'),
              stateKey: 'display_to_logged_users',
              isCheckbox: true,
            },
          ]}
        />

        <Grid item className={classes.textFieldContainer}>
          <Subject className={classes.subject} />
          <TextEditor
            initialValue={defaultValue?.message}
            title={i18n.t('ADD_MESSAGE_MAINTENANCE_KEY')}
            getValue={value =>
              handleChange({ ...defaultValue, message: value })
            }
            readOnly={disabled}
            maxLength={800}
          />
        </Grid>
      </Grid>
    </ToggleCard>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected },
  clients: { clientSelected },
}: ApplicationState) => ({
  clientSelected,
  companySelected,
});

export default connect(mapStateToProps, {})(Form);
